import { z } from 'zod';
import { fromError } from 'zod-validation-error';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import { AppError } from '@/lib/error-handling';
import { commonErrors } from '@/lib/error-handling/error-management';
import { isBrowser } from '@/utils/is';

const PUBLIC_PREFIX = 'PUBLIC_' as const;

const serverEnvSchema = z.object({
	LOG_LEVEL: z.enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal']).default('info'),
	// AEM Endpoints
	ES_DXP_PAGE_DATA_URL: z.url(),
	FR_DXP_PAGE_DATA_URL: z.url(),
	// A2D Dealer API
	DEALERS_A2D_URL: z.url(),
	MSAL_A2D_CLIENT_ID: z.string().min(1),
	MSAL_A2D_AUTHORITY_URL: z.url(),
	MSAL_A2D_CLIENT_SECRET: z.string().min(1),
	// Allocation Engine
	AE_URL: z.url(),
	AE_INVENTORY_SCOPE: z.string().min(1),
	AE_SCOPE: z.string().min(1),
	AE_ENCRYPTION_KEY: z.string().length(64),
	AE_ENCRYPTION_IV: z.string().length(32),
	AE_AZURE_AD_CLIENT_ID: z.string().min(1),
	AE_AZURE_AD_CLIENT_SECRET: z.string().min(1),
	AE_AZURE_AD_TENANT_ID: z.string().min(1),
	// CarDB
	CARDB_URL: z.url(),
	// Rockar
	ROCKAR_FUSIONAUTH_BASE_URL: z.url(),
	ROCKAR_FUSIONAUTH_API_KEY: z.string().min(1),
	ROCKAR_FUSIONAUTH_APP_ID: z.string().min(1),
	ROCKAR_FUSIONAUTH_LOGIN_ID: z.string().min(1),
	ROCKAR_FUSIONAUTH_PASSWORD: z.string().min(1),
	ROCKAR_GRAPHQL_URL: z.url(),
	ROCKAR_ENCRYPTION_KEY: z.string().length(64),
	ROCKAR_ENCRYPTION_IV: z.string().length(32),
	ROCKAR_SESSION_SECRET: z.string().min(1),
	// Azure AD Auth
	AZURE_AD_CLIENT_ID: z.string().min(1),
	AZURE_AD_CLIENT_SECRET: z.string().min(1),
	AZURE_AD_PROFILE_EXTENSION_ATTRIBUTE_EMPLOYEE_ID: z.string().min(1),
	AZURE_AD_TENANT_ID: z.string().min(1),
	// TFS Makolab
	TFS_MAKOLAB_STANDARD_API_URL: z.url(),
	ES_MSAL_TFS_CLIENT_ID: z.string().min(1),
	ES_MSAL_TFS_AUTHORITY_URL: z.url(),
	ES_MSAL_TFS_CLIENT_SECRET: z.string().min(1),
	FR_MSAL_TFS_CLIENT_ID: z.string().min(1),
	FR_MSAL_TFS_AUTHORITY_URL: z.url(),
	FR_MSAL_TFS_CLIENT_SECRET: z.string().min(1),

	// TFS OFS
	ES_OFS_URL: z.url(),
	FR_OFS_URL: z.url(),

	// Next Auth
	NEXTAUTH_SECRET: z.string().min(1),
	ES_NEXTAUTH_URL: z.url(),
	FR_NEXTAUTH_URL: z.url(),
	// IDR
	IDR_URL: z.url(),
	// PDF service
	PDF_SERVICE_URL: z.string().min(1),
	// PDF origin base URL (Only used in development as CP cannot reach localhost)
	PDF_ORIGIN_BASE_URL_DEV: z.url().optional(),
	// Customer Portal
	CUSTOMER_PORTAL_URL: z.url(),
	// HMAC key for encrypting user email
	USER_EMAIL_HMAC_KEY: z.string().min(1)
});

const clientEnvSchema = z.object({
	// CCIS
	PUBLIC_CCIS_BASE_URL: z.url(),
	// AEM Translations Endpoints
	PUBLIC_ES_TRANSLATIONS_URL: z.url(),
	PUBLIC_FR_TRANSLATIONS_URL: z.url(),
	// Mapbox
	PUBLIC_MAPBOX_ACCESS_TOKEN: z.string().min(1),
	PUBLIC_MAPBOX_URL: z.url(),
	PUBLIC_MAPBOX_MATRIX_URL: z.url(),
	// Makolab Insurance
	PUBLIC_MAKOLAB_INSURANCE_URL: z.url(),
	// JP Morgan
	PUBLIC_JPMMPS_SCRIPT_URL: z.url(),
	// CP Login Resources
	PUBLIC_LOGIN_SETTINGS_ES_URL: z.url(),
	PUBLIC_LOGIN_SETTINGS_FR_URL: z.url(),
	PUBLIC_LOGIN_SCRIPT_ES_URL: z.url(),
	PUBLIC_LOGIN_SCRIPT_FR_URL: z.url(),
	// Azure AD Auth
	PUBLIC_AZURE_AD_TENANT_ID: z.string().min(1),
	// Trade in form
	PUBLIC_FR_TRADE_IN_FORM_URL: z.url(),

	// Optional days added to AE cpEta before week/month display (invalid/missing → 0)
	PUBLIC_AE_ETA_BUFFER_WEEK: z.coerce.number().min(0).catch(0),
	PUBLIC_AE_ETA_BUFFER_MONTH: z.coerce.number().min(0).catch(0),
	PUBLIC_AE_ETA_BUFFER_FALLBACK: z.coerce.number().min(0).catch(5)
});

export const PUBLIC_ENV_KEY = '__ENV';

/**
 * Returns a filtered list of public environment variables.
 */
export function getPublicEnv() {
	const regex = new RegExp(`^${PUBLIC_PREFIX}`, 'i');

	const publicEnv = Object.keys(process.env)
		.filter((key) => regex.test(key))
		.reduce((env, key) => ({ ...env, [key]: process.env[key] }), {} as Record<string, string | undefined>);

	return publicEnv;
}

type CreateEnvOptions = {
	onInvalidAccess?: (envKey: string) => never;
	onValidationError?: (error: z.ZodError) => never;
	publicPrefix: string;
	windowPublicEnvKey: string;
};

/**
 * Creates a function that reads environment variables from the server or the browser.
 * TODO: If there are missing environment variables, the server should not start.
 * Currently, the server starts and logs several times the same error.
 */
export const createEnv = <TServerEnvSchema extends z.ZodObject<z.ZodRawShape>, TClientEnvSchema extends z.ZodObject<z.ZodRawShape>>(
	serverEnvSchema: TServerEnvSchema,
	clientEnvSchema: TClientEnvSchema,
	options: CreateEnvOptions
) => {
	type ServerEnv = z.infer<typeof serverEnvSchema>;
	type ClientEnv = z.infer<typeof clientEnvSchema>;
	type Env = ServerEnv & ClientEnv;

	const onInvalidAccess =
		options.onInvalidAccess ??
		((_envKey: string) => {
			throw new AppError({
				name: commonErrors.EnvServerAccess.name,
				message: commonErrors.EnvServerAccess.message,
				code: commonErrors.EnvServerAccess.code
			});
		});

	const onValidationError =
		options.onValidationError ??
		((error: z.ZodError) => {
			const validationError = fromError(error);
			console.log(JSON.stringify({ level: 50, time: Date.now(), msg: validationError.toString() })); // Mostly for DataDog ingestion
			throw new AppError({ name: commonErrors.EnvInvalid.name, message: validationError.toString(), code: commonErrors.EnvInvalid.code });
		});

	/**
	 * Reads a safe environment variable from the browser or any environment
	 * variable from the server (process.env).
	 *
	 * Usage:
	 * ```ts
	 * const API_URL = env('PUBLIC_API_URL');
	 * ```
	 */
	const env = <TEnvKey extends keyof Env = string>(envKey: TEnvKey): Env[TEnvKey] => {
		if (typeof envKey !== 'string') {
			throw new Error('envKey must be a string');
		}
		if (isBrowser()) {
			if (!envKey.startsWith(options.publicPrefix)) {
				return onInvalidAccess(envKey);
			}

			if (!window[options.windowPublicEnvKey as keyof Window]) {
				// If the public env is not in the window object for whatever reason this gets triggered and gets in the way of the actual issue
				// when the Next error boundry catches the error and shows the error UI. This cheeck is to prevent that and only log/show the error in development.
				if (process.env.NODE_ENV !== 'development') {
					throw new Error(`Public env not found in window object. Check for server side issues.`);
				}
				// This should be a .log not .error because if its .error Next will catch it and show the error UI.
				console.log(`Public env not found in window object. Check for server side issues.`);
				return {} as Env[TEnvKey];
			}

			const parsed = clientEnvSchema.safeParse(window[options.windowPublicEnvKey as keyof Window]);
			if (!parsed.success) {
				return onValidationError(parsed.error);
			}
			return parsed.data[envKey] as Env[TEnvKey];
		}

		const parsed = serverEnvSchema.extend(clientEnvSchema.shape).safeParse(process.env);
		if (!parsed.success) {
			if (process.env.NEXT_PHASE !== PHASE_PRODUCTION_BUILD) {
				return onValidationError(parsed.error);
			}

			console.warn(`env: ${envKey} called during build time`);
			return '' as Env[TEnvKey];
		}
		return parsed.data[envKey] as Env[TEnvKey];
	};

	return env;
};

export const env = createEnv(serverEnvSchema, clientEnvSchema, { publicPrefix: PUBLIC_PREFIX, windowPublicEnvKey: PUBLIC_ENV_KEY });
